import React, { useState } from 'react'
import { Contact } from './ApplicationWizard'
import { ContactForm } from './ContactForm'
import { jobApplicationService } from '../services/jobApplicationService'
import { MessageBox, useMessageBox } from './MessageBox'
import './ContactsSection.css'

interface ContactsSectionProps {
  contacts: Contact[]
  jobApplicationId: string
  onContactsChange: () => void
  viewMode?: boolean
}

export const ContactsSection: React.FC<ContactsSectionProps> = ({
  contacts,
  jobApplicationId,
  onContactsChange,
  viewMode = false,
}) => {
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const { messageBox, showMessageBox } = useMessageBox()

  const handleAddContact = async (contact: Contact) => {
    try {
      await jobApplicationService.addContact(jobApplicationId, contact)
      showMessageBox({
        type: 'success',
        title: 'Success',
        message: 'Contact added successfully',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
      setShowAddForm(false)
      onContactsChange()
    } catch (error) {
      showMessageBox({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to add contact',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
    }
  }

  const handleUpdateContact = async (contact: Contact) => {
    if (!contact.id) {
      showMessageBox({
        type: 'error',
        title: 'Error',
        message: 'Contact ID is required',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
      return
    }

    try {
      await jobApplicationService.updateContact(jobApplicationId, contact.id, contact)
      showMessageBox({
        type: 'success',
        title: 'Success',
        message: 'Contact updated successfully',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
      setEditingContact(null)
      onContactsChange()
    } catch (error) {
      showMessageBox({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update contact',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
    }
  }

  const handleDeleteContact = async (contactId: string) => {
    try {
      await jobApplicationService.deleteContact(jobApplicationId, contactId)
      showMessageBox({
        type: 'success',
        title: 'Success',
        message: 'Contact deleted successfully',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
      onContactsChange()
    } catch (error) {
      showMessageBox({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete contact',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
    }
  }

  const getContactTypeLabel = (type: Contact['contactType']): string => {
    const labels: Record<Contact['contactType'], string> = {
      job_poster: 'Job Poster',
      recruiter: 'Recruiter',
      hiring_manager: 'Hiring Manager',
      interviewer: 'Interviewer',
      other: 'Other',
    }
    return labels[type] || 'Other'
  }

  const getContactTypeClass = (type: Contact['contactType']): string => {
    const classes: Record<Contact['contactType'], string> = {
      job_poster: 'contact-type-badge--job-poster',
      recruiter: 'contact-type-badge--recruiter',
      hiring_manager: 'contact-type-badge--hiring-manager',
      interviewer: 'contact-type-badge--interviewer',
      other: 'contact-type-badge--other',
    }
    return classes[type] || 'contact-type-badge--other'
  }

  const handleDeleteContactConfirm = (contactId: string) => {
    showMessageBox({
      type: 'confirm',
      title: 'Delete contact?',
      message: 'Are you sure you want to delete this contact?',
      buttons: ['cancel', 'yes'],
      onButtonClick: (button) => {
        if (button === 'yes') {
          void handleDeleteContact(contactId)
        }
      }
    })
  }

  if (showAddForm || editingContact) {
    return (
      <div className="contacts-section">
        <ContactForm
          contact={editingContact}
          onSave={editingContact ? handleUpdateContact : handleAddContact}
          onCancel={() => {
            setShowAddForm(false)
            setEditingContact(null)
          }}
        />
        <MessageBox {...messageBox} />
      </div>
    )
  }

  return (
    <div className="contacts-section">
      <div className="contacts-section-header">
        <h3 className="contacts-section-title">
          <span className="contacts-icon">👥</span>
          Contacts ({contacts.length})
        </h3>
        {!viewMode && (
          <button
            className="add-contact-button"
            onClick={() => setShowAddForm(true)}
            title="Add Contact"
          >
            + Add Contact
          </button>
        )}
      </div>

      {contacts.length === 0 ? (
        <div className="contacts-empty">
          <p>No contacts added yet.</p>
          {!viewMode && (
            <button
              className="add-contact-button-inline"
              onClick={() => setShowAddForm(true)}
            >
              Add Your First Contact
            </button>
          )}
        </div>
      ) : (
        <div className="contacts-list">
          {contacts.map((contact) => (
            <div key={contact.id || Math.random()} className="contact-card">
              <div className="contact-card-header">
                <div className="contact-card-info">
                  <div className="contact-name-row">
                    <h4 className="contact-name">
                      {contact.name || 'Unnamed Contact'}
                    </h4>
                    {contact.extracted && (
                      <span className="extracted-badge" title="Extracted from job description">
                        Auto
                      </span>
                    )}
                  </div>
                  {contact.title && (
                    <p className="contact-title">{contact.title}</p>
                  )}
                  <span
                    className={`contact-type-badge ${getContactTypeClass(contact.contactType)}`}
                  >
                    {getContactTypeLabel(contact.contactType)}
                  </span>
                </div>
                {!viewMode && (
                  <div className="contact-card-actions">
                    <button
                      className="edit-contact-button"
                      onClick={() => setEditingContact(contact)}
                      title="Edit Contact"
                    >
                      ✏️
                    </button>
                    <button
                      className="delete-contact-button"
                      onClick={() =>
                        contact.id && handleDeleteContactConfirm(contact.id)
                      }
                      title="Delete Contact"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>

              <div className="contact-card-body">
                {contact.email && (
                  <div className="contact-info-item">
                    <span className="contact-info-label">📧</span>
                    <a
                      href={`mailto:${contact.email}`}
                      className="contact-info-value"
                    >
                      {contact.email}
                    </a>
                  </div>
                )}

                {contact.phone && (
                  <div className="contact-info-item">
                    <span className="contact-info-label">📞</span>
                    <a
                      href={`tel:${contact.phone}`}
                      className="contact-info-value"
                    >
                      {contact.phone}
                    </a>
                  </div>
                )}

                {contact.linkedinUrl && (
                  <div className="contact-info-item">
                    <span className="contact-info-label">💼</span>
                    <a
                      href={contact.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-info-value"
                    >
                      LinkedIn Profile
                    </a>
                  </div>
                )}

                {contact.notes && (
                  <div className="contact-notes">
                    <p className="contact-notes-label">Notes:</p>
                    <p className="contact-notes-text">{contact.notes}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <MessageBox {...messageBox} />
    </div>
  )
}
